import { useEffect, useState, useCallback } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Pagination, { PAGE_SIZE } from "@/components/Pagination";
import { Plus, Pencil, Trash2, Info, Eye, EyeOff } from "lucide-react";

const empty = { name: "", username: "", phone: "", email: "", pin: "", role: "agent", active: true };

export default function Agents() {
  const [list, setList] = useState([]);
  const { confirm } = useConfirm();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [showPin, setShowPin] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(() => api.get("/agents").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const paged = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const save = async () => {
    try {
      if (editId) {
        const body = { name: form.name, username: form.username, phone: form.phone, email: form.email, role: form.role, active: form.active };
        if (form.pin) body.pin = form.pin;
        await api.put(`/agents/${editId}`, body);
        toast.success("Agent mis à jour");
      } else {
        await api.post("/agents", form);
        toast.success(`Agent créé. Il se connecte avec : ${form.username.trim()} + son code PIN.`);
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (a) => { try { await api.put(`/agents/${a.id}`, { active: !a.active }); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (id) => { if (!(await confirm({ title: "Supprimer l'agent", description: "Supprimer cet agent ? Cette action est irréversible.", confirmText: "Supprimer", danger: true }))) return; try { await api.delete(`/agents/${id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(apiError(e)); } };

  const selCls = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">Agents</h1>
        <Button data-testid="add-agent-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="bg-sky-600 hover:bg-sky-700"><Plus size={18} className="mr-2" /> Nouvel agent</Button>
      </div>

      <Card className="p-4 bg-sky-50 border-sky-100 flex gap-3">
        <Info size={20} className="text-sky-600 shrink-0 mt-0.5" />
        <div className="text-sm text-sky-900 space-y-1">
          <p>Chaque agent se connecte avec son <b>nom d'utilisateur</b> + son <b>code PIN</b> définis ici (le téléphone sert uniquement de contact).</p>
          <p><b>Rôles :</b> l'<b>Admin</b> gère le paramétrage et voit tout ; l'<b>Agent</b> gère le quotidien (commandes, clients, paiements, caisse).</p>
          <p><b>PIN oublié ?</b> Cliquez sur ✏️ pour modifier l'agent et saisir un nouveau PIN.</p>
        </div>
      </Card>

      <Card className="overflow-hidden hidden md:block">
        <table className="data-table w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="px-4 py-3">Nom</th><th className="px-4 py-3">Identifiant</th><th className="px-4 py-3">Téléphone</th><th className="px-4 py-3">Rôle</th><th className="px-4 py-3">Actif</th><th className="px-4 py-3 text-right">Actions</th></tr></thead>
          <tbody>
            {paged.map((a) => (
              <tr key={a.id} data-testid={`agent-row-${a.id}`} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium text-slate-800">{a.name}</td>
                <td className="px-4 py-3 font-mono text-slate-700">{a.username}</td>
                <td className="px-4 py-3 text-slate-500">{a.phone}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${a.role === "admin" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>{a.role}</span></td>
                <td className="px-4 py-3"><Switch data-testid={`toggle-agent-${a.id}`} checked={a.active} onCheckedChange={() => toggle(a)} /></td>
                <td className="px-4 py-3 text-right space-x-1">
                  <Button size="icon" variant="ghost" data-testid={`edit-agent-${a.id}`} onClick={() => { setForm({ name: a.name, username: a.username || "", phone: a.phone || "", email: a.email || "", pin: "", role: a.role, active: a.active }); setEditId(a.id); setOpen(true); }}><Pencil size={16} /></Button>
                  <Button size="icon" variant="ghost" className="text-red-600" data-testid={`delete-agent-${a.id}`} onClick={() => remove(a.id)}><Trash2 size={16} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="md:hidden space-y-3">
        {paged.map((a) => (
          <Card key={a.id} data-testid={`agent-card-${a.id}`} className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 truncate">{a.name}</p>
                <p className="font-mono text-sm text-slate-600 truncate">{a.username}</p>
                {a.phone && <p className="text-sm text-slate-500 truncate">{a.phone}</p>}
                <span className={`inline-block mt-2 text-xs px-2 py-1 rounded ${a.role === "admin" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>{a.role}</span>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <div className="flex items-center gap-1 text-xs text-slate-400">Actif <Switch data-testid={`toggle-agent-${a.id}`} checked={a.active} onCheckedChange={() => toggle(a)} /></div>
                <div className="flex gap-1">
                  <Button size="icon" variant="outline" data-testid={`edit-agent-${a.id}`} onClick={() => { setForm({ name: a.name, username: a.username || "", phone: a.phone || "", email: a.email || "", pin: "", role: a.role, active: a.active }); setEditId(a.id); setOpen(true); }}><Pencil size={16} /></Button>
                  <Button size="icon" variant="outline" className="text-red-600" data-testid={`delete-agent-${a.id}`} onClick={() => remove(a.id)}><Trash2 size={16} /></Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Pagination page={page} total={list.length} onPage={setPage} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Modifier l'agent" : "Nouvel agent"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom complet</Label><Input data-testid="agent-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Nom d'utilisateur (pour la connexion)</Label><Input data-testid="agent-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="ex: marie, agent1..." /></div>
            <div><Label>Téléphone (contact, optionnel)</Label><Input data-testid="agent-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Email (pour réinitialisation du mot de passe)</Label><Input data-testid="agent-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ex: marie@email.com" /></div>
            <div><Label>Code PIN {editId && <span className="text-xs text-slate-400">(laisser vide pour ne pas changer)</span>}</Label>
              <div className="relative">
                <Input data-testid="agent-pin" type={showPin ? "text" : "password"} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} className="pr-10" />
                <button type="button" data-testid="toggle-agent-pin" onClick={() => setShowPin(!showPin)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label={showPin ? "Masquer" : "Afficher"}>
                  {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div><Label>Rôle</Label>
              <select data-testid="agent-role" className={selCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="agent">Agent</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Actif</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-agent-btn" onClick={save} className="bg-sky-600 hover:bg-sky-700">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
